import React, { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';
import './message-input.scss';
import { useMessageInput } from '../hooks/use-message-input';
import { CircularProgress } from '@mui/material';
import { SVGS } from '../../assets';
import { ReactComponent as SendMessage } from '@zonofi/svg-icons/send-message.svg';
import { ReactComponent as ExpandMore } from '@zonofi/svg-icons/arrow_down.svg';
import { ReactComponent as ExpandLess } from '@zonofi/svg-icons/arrow_up.svg';
import { ReactComponent as Attachment } from '@zonofi/svg-icons/attachment.svg';
import { ReactComponent as FileIcon } from '@zonofi/svg-icons/file-new.svg';
import { ReactComponent as CrossIcon } from '@zonofi/svg-icons/cross-icon.svg';
import _ from 'lodash';

enum ConversationSendMessageDropdown {
  SEND_TO_CUSTOMER = 'sendToCustomer',
  INTERNAL_NOTES = 'internalNotes',
}

interface MessageInputProps {
  channelId: string;
  conversationId: string;
  handleSendMsg: () => void;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  channelId,
  conversationId,
  handleSendMsg,
}) => {
  const {
    sendMessage,
    handleInputChange: onInputChange,
    text,
    isValidInputText,
    loader,
    inputDisabled,
    onUploadMedia,
    file,
    setFile,
  } = useMessageInput({
    channelId,
    conversationId,
    handleSendMsg,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMultiline, setIsMultiline] = useState<boolean>(false);

  const [selectedOption, setSelectedOption] =
    useState<ConversationSendMessageDropdown>(
      ConversationSendMessageDropdown.SEND_TO_CUSTOMER
    );

  const [expandedDropdownOptions, setExpandedDropdownOptions] =
    useState<boolean>(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const dropDownOptions: Record<
    ConversationSendMessageDropdown,
    { icon: string; text: string }
  > = {
    [ConversationSendMessageDropdown.SEND_TO_CUSTOMER]: {
      icon: SVGS.UserIcon,
      text: 'Send To Customer',
    },
    [ConversationSendMessageDropdown.INTERNAL_NOTES]: {
      icon: SVGS.Notepad,
      text: 'Internal Notes',
    },
  };

  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    try {
      const { value: newText } = event.target;
      onInputChange(newText);
      const textarea = inputRef.current;

      if (textarea) {
        if (textarea.scrollHeight > textarea.clientHeight) {
          setIsMultiline(true); // Content has flowed to a second line
        } else {
          setIsMultiline(false); // Content fits on a single line
        }
      }
      event.target.style.height = 'auto';
      event.target.style.height = event.target.scrollHeight + 'px';
    } catch (e) {
      console.error(e);
    }
  };
  const handleKeyPress = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    try {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendMessage?.(selectedOption);
        inputRef.current?.setAttribute('rows', '1');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSelectMode = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedOption !== key)
      (() => {
        setSelectedOption(key as ConversationSendMessageDropdown);
        setExpandedDropdownOptions(false);
      })();
    else
      (() => {
        if (navigator.vibrate) {
          navigator.vibrate(100);
        }
      })();
  };

  return (
    <div className="message-input">
      {/* MODE */}
      <span
        className={`message-input-mode ${
          expandedDropdownOptions ? 'expanded' : ''
        }`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setExpandedDropdownOptions(true);
        }}
      >
        {expandedDropdownOptions && (
          <>
            <div className="message-input-mode-dropdown-container">
              {Object.entries(dropDownOptions).map(([key, value]) => (
                <span
                  key={key}
                  className={`message-input-mode-dropdown-container-item ${
                    selectedOption === key ? 'selected' : ''
                  }`}
                  onClick={(e) => handleSelectMode(e, key)}
                >
                  <img src={value.icon} width={20} height={20} />
                  {value.text}
                </span>
              ))}
            </div>

            <div
              className="message-input-mode-backdrop"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setExpandedDropdownOptions(false);
              }}
            />
          </>
        )}
        <img src={SVGS.UserIcon} width={14} height={14} alt="user icon" />
        {dropDownOptions[selectedOption].text}
        {!expandedDropdownOptions ? (
          <ExpandMore width={16} height={16} />
        ) : (
          <ExpandLess width={16} height={16} />
        )}
      </span>
      <div className="message-input-container">
        <div className="message-input-container-input-wrapper">
          {/* FILE PREVIEW */}
          {file?.length > 0 && (
            <div className="message-input-preview">
              {file.map((f, index) => {
                const isImage = f.file.type.startsWith('image');

                return (
                  <div key={index} className="message-input-preview-item">
                    {isImage ? (
                      <img
                        src={f.uri as string}
                        alt="preview"
                        className="message-input-preview-image"
                      />
                    ) : (
                      <div className="message-input-preview-file">
                        <FileIcon height={18} />
                        <div className="font-regular">{f.name}</div>
                      </div>
                    )}

                    <span
                      className="message-input-preview-remove"
                      onClick={() => {
                        const newFiles = file?.filter(
                          (x) => x?.name !== f?.name
                        );
                        setFile(newFiles);
                      }}
                    >
                      <CrossIcon height={10} width={10} color="#fff" />
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* TEXTAREA */}
          <div
            className={`message-input-container-input ${
              selectedOption === ConversationSendMessageDropdown.INTERNAL_NOTES
                ? 'notes'
                : ''
            }`}
          >
            <textarea
              placeholder={
                selectedOption ===
                ConversationSendMessageDropdown.INTERNAL_NOTES
                  ? 'Send Notes'
                  : 'Add a Message'
              }
              className={`message-input-container-input-text-area ${
                selectedOption ===
                ConversationSendMessageDropdown.INTERNAL_NOTES
                  ? 'notes'
                  : ''
              } font-regular`}
              ref={inputRef}
              rows={1}
              value={text}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
            />

            <Attachment width={16} height={16} onClick={onUploadMedia} />
          </div>
        </div>

        {/* SEND BUTTON */}
        <button
          aria-label="Send Message Button"
          className="message-input-container-send"
          onClick={() => sendMessage?.(selectedOption)}
          disabled={!_.isEmpty(file) ? false : inputDisabled}
          title="Send Message"
        >
          {loader ? (
            <CircularProgress size={20} />
          ) : (
            <SendMessage
              width={24}
              height={24}
              color={
                isValidInputText() || !_.isEmpty(file) ? '#007AFF' : '#626F86'
              }
            />
          )}
        </button>
      </div>
    </div>
  );
};
